import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../../core/sdk';
import { MatenRequestResponse } from '../../../../shared';

// sort folders and trainings by their position
const sortByPos = (a: any, b: any) => {
    if (a.pos === b.pos) {
        return 0;
    }
    return a.pos < b.pos ? -1 : 1;
};

/**
 * Load all trainings and make it possible to manage them
 */
class TrainingOverviewStore {
    private sdk: ApplicationSdk;
    public folderListLoading: boolean = false;

    public listFolder: any[] = [];
    public listTraining: any[] = [];

    public s3url: any = {};
    public searchValue: string = '';
    public treeExpanded: any = {};

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            folderListLoading: observable,
            treeExpanded: observable,
            listFolder: observable,
            listTraining: observable,
            s3url: observable,
            searchValue: observable,
            clearSearch: action,
            updateSearch: action,
            setTreeExpanded: action,
            treeData: computed,
            isLoading: computed,
        });

        this.sdk = sdk;

        // Load state from localStorage
        this.loadFromLocalStorage();
    }

    public async init(): Promise<void> {
        this.fetchTrainingFolderList();
    }

    public async fetchTrainingFolderList(): Promise<void> {
        runInAction(() => {
            this.folderListLoading = true;
        });

        const trainingListResult: any = await this.sdk.fetchApi('training', 'folder/query', {});

        runInAction(() => {
            this.folderListLoading = false;

            const data = trainingListResult?.json?.data || {};

            if (trainingListResult?.status === 200) {
                this.listFolder = data?.folders || [];
                this.listTraining = data?.trainings || [];
                this.s3url = trainingListResult?.json?.s3url || {};
            }
        });
    }

    public async updateTreeSortOrder(
        folder_id: number | undefined = undefined,
        training_id: number | undefined = undefined,
        new_parent_id: number,
        newSortOrder: any
    ): Promise<void> {
        runInAction(() => {
            this.folderListLoading = true;
        });

        const folderSortOrderResult: any = await this.sdk.fetchApi('training', 'folder/sortorder', {
            folder_id,
            training_id,
            parent_id: new_parent_id,
            sort_order: newSortOrder,
        });

        runInAction(() => {
            this.folderListLoading = false;

            const data = folderSortOrderResult?.json?.data || {};

            if (folderSortOrderResult?.status === 200) {
                this.listFolder = data?.folders || [];
                this.listTraining = data?.trainings || [];
                this.s3url = folderSortOrderResult?.json?.s3url || {};
            }
        });
    }

    get treeData(): any[] {
        const folderMap = new Map();

        // Prepare all folders
        this.listFolder.forEach((folder: any) => {
            folderMap.set(folder.id, {
                id: folder.id,
                parent: folder.parent_id,
                key: `folder-${folder.id}`,
                label: folder.name,
                type: 'folder',
                pos: folder.pos,
                data: folder.description,
                visible: folder.visible,
                img: folder.image ? this.s3urlFolder + folder.image + '.jpeg' : null,
                deleteable: true,
                children: [],
                warning: !folder.visible, // Initialize warning based on visibility
            });
        });

        // Sort trainings into folders
        this.listTraining.forEach((training: any) => {
            const parent = folderMap.get(training.folder_id);
            if (parent) {
                parent.children.push({
                    id: training.id,
                    parent: training.folder_id,
                    key: `training-${training.id}`,
                    label: training.name,
                    pos: training.pos,
                    type: 'training',
                    data: training.description,
                    img: training.image ? this.s3urlTraining + training.image + '.jpeg' : null,
                    visible: training.status === 1,
                    deleteable: true,
                    leaf: true,
                    warning: !parent.visible, // Inherit warning from parent
                });
            }
        });

        // Build hierarchy and propagate warnings
        const tree: any[] = [];
        this.listFolder.forEach((folder: any) => {
            const node = folderMap.get(folder.id);
            if (folder.parent_id) {
                const parent = folderMap.get(folder.parent_id);
                if (parent) {
                    parent.children.push(node);

                    // Propagate warning if parent is not visible
                    if (!parent.visible || parent.warning) {
                        node.warning = true;
                    }
                }
            } else {
                tree.push({
                    ...node,
                    deleteable: false,
                });
            }
        });

        // Apply filter logic if a search value is present
        if (this.searchValue.trim() !== '') {
            const filterTree = (nodes: any[]): any[] => {
                return nodes
                    .map(node => {
                        // Check if the current node or its children match the search value
                        if (node.label.toLowerCase().includes(this.searchValue.toLowerCase())) {
                            return node; // Keep the node if it matches
                        }
                        if (node.children) {
                            const filteredChildren = filterTree(node.children);
                            if (filteredChildren.length > 0) {
                                return { ...node, children: filteredChildren }; // Keep the node if children match
                            }
                        }
                        return null; // Remove the node if it doesn't match
                    })
                    .filter(node => node !== null); // Remove all `null` values
            };

            return filterTree(tree);
        }

        // Sort the whole tree and its children by position
        const sortTree = (nodes: any[]): any[] => {
            return nodes
                .map(node => {
                    if (node.children) {
                        node.children = sortTree(node.children);
                    }
                    return node;
                })
                .sort(sortByPos);
        };

        return sortTree(tree);
    }

    get isLoading(): boolean {
        return this.folderListLoading;
    }

    public clearSearch(): void {
        if (this.searchValue) {
            this.searchValue = '';
            this.saveToLocalStorage();
        }
    }

    private saveToLocalStorage(): void {
        const state = {
            // sortOrder: this.sortOrder,
            // sortField: this.sortField,
            searchValue: this.searchValue,
            tree: this.treeExpanded,
        };
        localStorage.setItem('training', JSON.stringify(state));
    }

    private loadFromLocalStorage(): void {
        const savedState = localStorage.getItem('training');
        if (savedState) {
            try {
                const { searchValue, tree } = JSON.parse(savedState);
                runInAction(() => {
                    // this.sortOrder = sortOrder || 1;
                    // this.sortField = sortField || 'id';
                    this.searchValue = searchValue || '';
                    this.treeExpanded = tree || {};
                    // this.filterCategories = filterCategories || [];
                });
            } catch (error) {
                console.error('Failed to load state from localStorage:', error);
            }
        }
    }

    public updateSearch(value: string): void {
        this.searchValue = value;

        // Save updated state to localStorage
        this.saveToLocalStorage();

        // if (this.timeout) {
        //     clearTimeout(this.timeout);
        // }

        // if (SEARCH_DELAY) {
        //     this.timeout = setTimeout(() => {
        //         this.fetchProductList();
        //     }, SEARCH_DELAY);
        // }
    }

    public async onSearch(): Promise<void> {
        // if (this.timeout) {
        //     clearTimeout(this.timeout);
        // }
        // await this.fetchProductList();
    }

    public setTreeExpanded(value: any): void {
        this.treeExpanded = value;
        // Save updated state to localStorage
        this.saveToLocalStorage();
    }

    public onLeaveStore(): void {}

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'editfolder') {
            if (event?.event === 'NEW') {
                this.fetchTrainingFolderList();
            } else if (event?.event === 'UPDATE') {
                this.fetchTrainingFolderList();
            }
        } else if (dialog === 'edittraining') {
            this.fetchTrainingFolderList();
        }
    }

    public async deleteTraining(training_id: number): Promise<MatenRequestResponse> {
        if (training_id) {
            const trainingData = {
                training_id,
            };
            const responseQuestion = await this.sdk.fetchApi('training', 'delete', trainingData);

            if (responseQuestion.status === 200) {
                return { success: true, status: 200, data: responseQuestion.json };
            }
        }

        return { success: false, status: 404 };
    }

    public async deleteFolder(folder_id: number): Promise<MatenRequestResponse> {
        if (folder_id) {
            try {
                const trainingData = {
                    folder_id,
                };
                const responseQuestion = await this.sdk.fetchApi(
                    'training',
                    'folder/delete',
                    trainingData
                );

                if (responseQuestion.status === 200) {
                    return { success: true, status: 200, data: responseQuestion.json };
                } else {
                    const errorCode = responseQuestion?.json?.error || '';

                    if (errorCode) {
                        switch (errorCode) {
                            case 'ERR_FOLDER_HAS_CHILDREN':
                                throw new Error(
                                    'Der Ordner kann nicht gelöscht werden, da er Unterordner oder Schulungen enthält.'
                                );
                            case 'ERR_FOLDER_HAS_TRAININGS':
                                throw new Error(
                                    'Der Ordner kann nicht gelöscht werden, da er Schulungen enthält.'
                                );
                            default:
                                break;
                        }
                    }

                    throw new Error('Ein unbekannter Fehler ist aufgetreten');
                }
            } catch (error: any) {
                return {
                    success: false,
                    status: 403,
                    error: error.message || 'Ein unbekannter Fehler ist aufgetreten',
                };
            }
        }
        return { success: false, status: 404 };
    }

    get s3urlFolder(): string {
        return this.s3url?.folder || '';
    }

    get s3urlTraining(): string {
        return this.s3url?.training || '';
    }
}
export default TrainingOverviewStore;
